<template>
  <ModalDialog v-model="showModal" size="md" :showXCloseButton="true" :closeOnEsc="false" :isLoading="isLoading">
    <FormKit v-if="ModalState.NEW_REGO == currentModalState" v-model="newRegoDetails" type="form" @submit="findRego">
      <RichText :field="changeRegistrationTitle.label" />
      <RACQFormKit :bindings="formInputData('registrationNumber')" />
      <InformationBox
        v-if="showValidationError"
        :type="InformationBoxTypes.ERROR"
        :title="validationError?.title?.value"
        :description="validationError?.content?.value"
      />
      <FormKit
        type="submit"
        :outerClass="showValidationError ? 'mt-8' : 'mt-2'"
        inputClass="btn-md btn w-full btn-primary"
        :label="updateBtn?.label?.value"
        :disabled="isLoading"
      />
    </FormKit>
    <div v-else-if="ModalState.COMPLETE == currentModalState">
      <RichText :field="{ value: completedText }" />
      <button class="mt-2 btn w-full btn-md btn-primary" @click="resetModal">{{ doneBtn?.label?.value }}</button>
    </div>
    <div v-else-if="ModalState.CMO_ERROR == currentModalState">
      <RichText :field="cmoError?.label" />
      <button class="mt-2 btn w-full btn-md btn-secondary" @click="resetModal">{{ closeBtn?.label?.value }}</button>
    </div>
    <div v-else-if="ModalState.EXPIRED_ERROR == currentModalState">
      <RichText :field="expiredError?.label" />
      <button class="mt-2 btn w-full btn-md btn-secondary" @click="resetModal">{{ closeBtn?.label?.value }}</button>
    </div>
  </ModalDialog>
</template>
<script lang="ts" setup>
import { useRouter } from 'vue-router';
import { watch, ref, onMounted, computed } from 'vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import { InsuranceVehicleDetail, ISitecoreFormGenericInput, OwnedAssistanceProduct } from '@/interfaces';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { getFormInput } from '@/utils/form-input-helper';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import { convertArrayToObject } from '@/utils/data-transforms';
import useFormInputData from '@/composables/useFormInputData';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import vehicleService from '@/services/store/vehicle.service';
import { RoadsideAssistanceService } from '@/services/store/roadside-assistance.service';
import { getRsaPolicyStatusDetails } from '@/utils/self-service-rules/RSAPolicyStatusRules';
import { useAccountStore, useAnalyticsStore } from '@/store';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { FormEvents } from '@/enums/form-events.enum';
import { OriginatingSystem } from '@/constants/originating-system.const';

enum ModalState {
  NEW_REGO = 'newRego',
  COMPLETE = 'complete',
  CMO_ERROR = 'cmoError',
  EXPIRED_ERROR = 'expiredError',
}

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  policy?: OwnedAssistanceProduct;
}>();

const changeRegistrationTitle = getFormInput(props.content, 'changeRegistrationTitle');
const updateBtn = getFormInput(props.content, 'updateBtn');
const closeBtn = getFormInput(props.content, 'closeBtn');
const doneBtn = getFormInput(props.content, 'doneBtn');
const cmoError = getFormInput(props.content, 'cmoError');
const expiredError = getFormInput(props.content, 'expiredError');
const validationError = getFormInput(props.content, 'validationError');

const formInputs = convertArrayToObject(props.content);
const { formInputData } = useFormInputData(formInputs);

const router = useRouter();
const accountStore = useAccountStore();
const analyticsStore = useAnalyticsStore();

const currentModalState = ref<ModalState>(ModalState.NEW_REGO);
const showModal = ref(false);
const showValidationError = ref(false);
const isLoading = ref(false);
const newRegoDetails = ref<{
  registrationNumber?: string;
}>({});

const completedText = computed(() => {
  const richText = getFormInput(props.content, 'success')?.label?.value as string;
  return replaceBookmark('newRego', richText, newRegoDetails.value.registrationNumber ?? '');
});

watch(
  () => router.currentRoute.value.hash,
  (newVal) => {
    // Start change rego modal
    if (newVal.includes('#changeRego')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      router.currentRoute.value.hash = '';
      showModal.value = true;

      //-------------------------------
      // Push analytics START
      //-------------------------------
      analyticsStore.pushAnalytics({
        event: FormEvents.START,
        form: {
          name: 'Update vehicle registration',
          stepName: 'FormStarted',
          productName: props.policy?.tierDescription,
        },
      });

      if (currentModalState.value != ModalState.NEW_REGO) {
        pushJeopardy('Invalid policy status');
      }
      //-------------------------------
      // Push analytics END
      //-------------------------------
    }
  },
  {
    deep: true,
  }
);

watch(showModal, (newVal, oldVal) => {
  if (!newVal && oldVal) resetModal();
});

const resetModal = () => {
  showModal.value = false;
  // Wait 1s for modal close animation to avoid users seeing flash
  setTimeout(() => {
    showValidationError.value = false;
    isLoading.value = false;
    newRegoDetails.value = {};
    if (!props.policy) return (currentModalState.value = ModalState.CMO_ERROR);
    const status = getRsaPolicyStatusDetails(props.policy!);
    if (status.isCMO || status.isFordNCO) currentModalState.value = ModalState.CMO_ERROR;
    else if (status.isExpiredOrLapse) currentModalState.value = ModalState.EXPIRED_ERROR;
    else currentModalState.value = ModalState.NEW_REGO;
  }, 1000);
};

onMounted(resetModal);

/**
 * Search registration number,
 * Update if matches
 */
const findRego = async () => {
  isLoading.value = true;
  try {
    newRegoDetails.value.registrationNumber = newRegoDetails.value.registrationNumber
      ?.toUpperCase()
      .replaceAll(' ', '');
    if (newRegoDetails.value.registrationNumber == props.policy?.vehicleDetail.registrationNumber?.toUpperCase())
      throw new Error('Same registration number');
    const vehicleDataFromRego = await vehicleService.regoLookup(newRegoDetails.value.registrationNumber!, OriginatingSystem.Roadside);
    showValidationError.value = !vehicleDataFromRego;
    vehicleDataFromRego && (await changeRego(vehicleDataFromRego));
  } catch {
    currentModalState.value = ModalState.CMO_ERROR;
    pushJeopardy('Unable to retrieve vehicle details');
  }
  isLoading.value = false;
};

/**
 * Validate and update rego
 *
 * @param vehicleDetail
 */
const changeRego = async (vehicleDetail: InsuranceVehicleDetail) => {
  // Validate rego
  const sameYear = vehicleDetail.manufacturingYear == props.policy?.vehicleDetail.manufacturingYear;
  const sameMake =
    vehicleDetail.make?.description
      ?.toLowerCase()
      .includes(props.policy?.vehicleDetail?.make?.description?.toLowerCase() ?? '') ||
    props.policy?.vehicleDetail?.make?.description
      ?.toLowerCase()
      .includes(vehicleDetail.make?.description?.toLowerCase() ?? '');
  const sameModel =
    vehicleDetail.model?.description
      ?.toLowerCase()
      .includes(props.policy?.vehicleDetail?.model?.description?.toLowerCase() ?? '') ||
    props.policy?.vehicleDetail?.model?.description
      ?.toLowerCase()
      .includes(vehicleDetail.model?.description?.toLowerCase());
  if (!sameYear || !sameMake || !sameModel) {
    pushJeopardy('Vehicle details mismatch');
    showValidationError.value = true;
    return;
  }

  // Update rego
  const policyId = router.currentRoute.value.query.policyId;
  await RoadsideAssistanceService.changeRego(newRegoDetails.value.registrationNumber!, policyId as string);
  currentModalState.value = ModalState.COMPLETE;

  // Update portfolio
  const product = accountStore.identityDetails?.portfolio?.assistanceProducts?.find(
    (product) => product.policyId == policyId && product.vehicleDetail
  );
  if (product?.policyId == policyId && product?.vehicleDetail) {
    product.vehicleDetail.registrationNumber = newRegoDetails.value.registrationNumber!;
  }

  analyticsStore.pushAnalytics({
    event: FormEvents.COMPLETE,
    form: {
      name: 'Update vehicle registration',
      stepName: 'FormCompleted',
      productName: props.policy?.tierDescription,
    },
  });
};

const pushJeopardy = (error: string) => {
  analyticsStore.pushAnalytics({
    event: FormEvents.JEOPARDY,
    form: {
      name: 'Update vehicle registration',
      formErrorType: error,
      productName: props.policy?.tierDescription,
    },
  });
};
</script>
